function isPrivateIpv4(hostname: string): boolean {
  const parts = hostname.split('.').map(Number)
  if (parts.length !== 4 || parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) {
    return false
  }
  const first = parts[0] as number
  const second = parts[1] as number
  return first === 0
    || first === 10
    || first === 127
    || (first === 100 && typeof second === 'number' && second >= 64 && second <= 127)
    || (first === 169 && second === 254)
    || (first === 172 && typeof second === 'number' && second >= 16 && second <= 31)
    || (first === 192 && second === 168)
    || (first === 192 && second === 0)
    || (first === 198 && (second === 18 || second === 19 || second === 51))
    || (first === 203 && second === 0)
    || first === 224
    || first >= 240
}

function isPrivateIpv6(hostname: string): boolean {
  const unwrapped = hostname.replace(/^\[|\]$/g, '').toLowerCase()
  if (!unwrapped.includes(':')) return false
  const first = Number.parseInt(unwrapped.split(':', 1)[0] || '0', 16)
  return unwrapped === '::'
    || unwrapped === '::1'
    || unwrapped.startsWith('::ffff:')
    || unwrapped.startsWith('2001:db8:')
    || (Number.isInteger(first) && (first & 0xfe00) === 0xfc00)
    || (Number.isInteger(first) && (first & 0xffc0) === 0xfe80)
    || (Number.isInteger(first) && (first & 0xffc0) === 0xfec0)
    || (Number.isInteger(first) && (first & 0xff00) === 0xff00)
}

/**
 * Cheap syntactic preflight shared by the client and Worker. The Worker must
 * still resolve hostnames and enforce redirect/IP checks before every fetch.
 */
export function isPublicLinkPreviewTarget(value: string): boolean {
  try {
    const target = new URL(value)
    const hostname = target.hostname.toLowerCase()
    return (target.protocol === 'http:' || target.protocol === 'https:')
      && !target.username
      && !target.password
      && hostname !== 'localhost'
      && !hostname.endsWith('.localhost')
      && !hostname.endsWith('.local')
      && !isPrivateIpv4(hostname)
      && !isPrivateIpv6(hostname)
  } catch {
    return false
  }
}

/**
 * What a site's interstitial calls itself while the page is not the page: a redirect notice ("Redirecting to www.airbnb.co.uk"), a bot or
 * access check, a missing page or a sign-in wall. Whole title only, so a page that merely mentions one of these still counts.
 */
const INTERSTITIAL_TITLE = /^(?:redirecting(?: to\b.*|\.{3}|…)?|just a moment(?:\.{3})?|attention required!?(?: \| .*)?|access denied|403 forbidden|forbidden|404(?: not found)?|page not found|not found|please wait(?:\.{3})?|loading(?:\.{3})?|verifying you are human.*|are you a robot\??|sign in|log in|login|untitled|error|an error occurred)$/i

/** The host a redirect notice names ("Redirecting to www.airbnb.co.uk" gives `www.airbnb.co.uk`), so the page can be asked for there. */
export function redirectNoticeHost(title: string | null | undefined): string | null {
  return /^redirecting to ([a-z0-9][a-z0-9.-]*[a-z0-9])\.?$/i.exec(title?.trim() ?? '')?.[1]?.toLowerCase() ?? null
}

/** Identifies provider/page-shell titles that do not describe the saved item. */
export function isProviderShellLinkTitle(targetUrl: string, value: string): boolean {
  const normalized = value.replace(/[\u200e\u200f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()
  if (INTERSTITIAL_TITLE.test(normalized)) return true
  let hostname = ''
  try {
    hostname = new URL(targetUrl).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return false
  }
  if (hostname === 'spotify.com' || hostname.endsWith('.spotify.com')) {
    return /^spotify(?:\s*[–—-]\s*web player)?$/i.test(value.trim())
  }
  if (hostname === 'imdb.com' || hostname.endsWith('.imdb.com')) {
    return /^tt\d{5,}$/i.test(value.trim())
      || normalized === 'imdb'
      || normalized.startsWith('imdb: ratings, reviews, and where to watch')
  }
  if (hostname === 'gemini.google.com' || hostname === 'share.gemini.google') {
    return /^(?:google )?gemini(?:\s*[-–—]\s*direct access to google ai)?$/.test(normalized)
  }
  if (hostname === 'google.com' || hostname.endsWith('.google.com') || hostname === 'maps.google.com') {
    return normalized === 'google maps'
  }
  if (hostname === 'github.com' || hostname.endsWith('.github.com')) return normalized === 'github'
  if (hostname === 'reddit.com' || hostname.endsWith('.reddit.com') || hostname === 'redd.it') {
    // What Reddit shows anything it takes for a bot (and its sign-in and robot checks) is not the page's name.
    return normalized === 'reddit' || normalized === 'welcome to reddit' || normalized === 'blocked'
      || /^reddit\s*[-–—]\s*(?:prove your humanity|dive into anything|the heart of the internet|please wait for verification)$/.test(normalized)
  }
  if (hostname === 'grok.com' || hostname.endsWith('.grok.com')) return normalized === 'grok' || normalized === 'shared grok conversation'
  if (hostname === 'chatgpt.com' || hostname === 'chat.openai.com') return normalized === 'chatgpt' || normalized === 'check out this chat'
  if (hostname === 'perplexity.ai' || hostname.endsWith('.perplexity.ai')) {
    return normalized === 'perplexity'
  }
  if (hostname === 'theatlantic.com' || hostname.endsWith('.theatlantic.com')) {
    return normalized === 'the atlantic'
  }
  return false
}
